import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Tabs, Tab } from '@material-ui/core';
import ReactMarkdown from 'react-markdown';
import { ModalWrapper } from '../ModalWrapper';
import { IAgentPromptLogData, IAgentPromptLogReference } from 'types/agentPromptLog';
import { getAgentPromptLogByEntity } from 'services/agentPromptLog';
import { formatServerTimestamp } from 'helpers/helpers';

export interface IRunLogReference {
  runNumber: number;
  logRef: IAgentPromptLogReference;
}

type IProps = {
  entityType: string;
  entityId: number;
  /** Optional: log references grouped by run (for grouped items with multiple runs) */
  runLogs?: IRunLogReference[];
  onClose: () => void;
};

interface ILogWithRun extends IAgentPromptLogData {
  runNumber: number;
}

const AgentPromptLogModal: React.FC<IProps> = ({ entityType, entityId, runLogs, onClose }) => {
  const [logsData, setLogsData] = useState<ILogWithRun[]>([]);
  const [selectedRunIndex, setSelectedRunIndex] = useState(0);
  const [selectedLogIndex, setSelectedLogIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    prompt: false,
    response: false,
    context: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);

        // If run logs are provided, fetch all of them with run info
        if (runLogs && runLogs.length > 0) {
          const allLogs: ILogWithRun[] = [];

          for (const runLog of runLogs) {
            const response = await getAgentPromptLogByEntity({
              entity_type: runLog.logRef.log_entity_type,
              entity_id: runLog.logRef.log_entity_id,
            });
            if (response.data && response.data.length > 0) {
              // Add run number to each log
              const logsWithRun = response.data.map((log) => ({
                ...log,
                runNumber: runLog.runNumber,
              }));
              allLogs.push(...logsWithRun);
            }
          }

          if (allLogs.length > 0) {
            setLogsData(allLogs);
            // Select the first run by default
            const firstRunNumber = Math.min(...allLogs.map((l) => l.runNumber));
            setSelectedRunIndex(runLogs.findIndex((r) => r.runNumber === firstRunNumber));
            setSelectedLogIndex(0);
          } else {
            setError('No log data found');
          }
        } else {
          // Single entity - original behavior
          const response = await getAgentPromptLogByEntity({ entity_type: entityType, entity_id: entityId });
          if (response.error) {
            setError(response.error);
          } else if (response.data && response.data.length > 0) {
            const logsWithRun = response.data.map((log) => ({ ...log, runNumber: 1 }));
            setLogsData(logsWithRun);
            setSelectedRunIndex(0);
            setSelectedLogIndex(logsWithRun.length - 1);
          } else {
            setError('No log data found');
          }
        }
      } catch (err) {
        setError('Failed to load log data');
        console.error('Error fetching agent prompt log:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [entityType, entityId, runLogs]);

  const getLogLabel = (log: IAgentPromptLogData): string => {
    // Try to get agent_step from context_data
    if (log.context_data && log.context_data.agent_step) {
      const stepLabel = log.context_data.agent_step.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
      return stepLabel;
    }
    // Fall back to agent_type
    return log.agent_type;
  };

  // Group logs by run number
  const logsByRun = logsData.reduce((acc, log) => {
    if (!acc[log.runNumber]) {
      acc[log.runNumber] = [];
    }
    acc[log.runNumber].push(log);
    return acc;
  }, {} as Record<number, ILogWithRun[]>);

  const runNumbers = Object.keys(logsByRun)
    .map(Number)
    .sort((a, b) => a - b);
  const hasMultipleRuns = runNumbers.length > 1;

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <i className="fa fa-spinner fa-spin" style={{ fontSize: 24, color: '#1976d2' }} />
          <LoadingText>Loading...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error || logsData.length === 0) {
      return (
        <ErrorContainer>
          <i className="fa fa-exclamation-triangle" style={{ fontSize: 24, color: '#f44336' }} />
          <ErrorText>{error || 'No data available'}</ErrorText>
        </ErrorContainer>
      );
    }

    const currentRunNumber = runNumbers[selectedRunIndex] || runNumbers[0];
    const currentRunLogs = logsByRun[currentRunNumber] || [];
    const logData = currentRunLogs[selectedLogIndex] || currentRunLogs[0];

    if (!logData) {
      return (
        <ErrorContainer>
          <ErrorText>No log data available for this run</ErrorText>
        </ErrorContainer>
      );
    }

    return (
      <ContentContainer>
        {/* Run tabs - show only if multiple runs */}
        {hasMultipleRuns && (
          <RunTabsContainer>
            <RunTabsLabel>Run:</RunTabsLabel>
            <StyledTabs
              value={selectedRunIndex}
              onChange={(_, newValue) => {
                setSelectedRunIndex(newValue);
                setSelectedLogIndex(0);
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {runNumbers.map((runNum) => (
                <StyledTab key={runNum} label={`#${runNum}`} />
              ))}
            </StyledTabs>
          </RunTabsContainer>
        )}

        {/* Log step tabs within the run */}
        {currentRunLogs.length > 1 && (
          <StyledTabs
            value={selectedLogIndex}
            onChange={(_, newValue) => setSelectedLogIndex(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {currentRunLogs.map((log, index) => (
              <StyledTab
                key={log.id}
                label={
                  <TabLabel>
                    {getLogLabel(log)}
                    <TabStatus status={log.status}>
                      {log.status === 'success' ? '✓' : log.status === 'error' ? '✗' : '•'}
                    </TabStatus>
                  </TabLabel>
                }
              />
            ))}
          </StyledTabs>
        )}

        <Section>
          <SectionHeader>
            <SectionTitle>Agent Information</SectionTitle>
            <HeaderRight>
              <Timestamp>{formatServerTimestamp(logData.created_at)}</Timestamp>
              <StatusBadge status={logData.status}>{logData.status.toUpperCase()}</StatusBadge>
            </HeaderRight>
          </SectionHeader>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Agent Type:</InfoLabel>
              <InfoValue>{logData.agent_type}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Model:</InfoLabel>
              <InfoValue>{logData.model}</InfoValue>
            </InfoRow>
            {logData.execution_time_ms && (
              <InfoRow>
                <InfoLabel>Execution Time:</InfoLabel>
                <InfoValue>
                  {logData.execution_time_ms >= 1000
                    ? `${(logData.execution_time_ms / 1000).toFixed(2)}s (${logData.execution_time_ms}ms)`
                    : `${logData.execution_time_ms}ms`}
                </InfoValue>
              </InfoRow>
            )}
            <InfoRow>
              <InfoLabel>Temperature:</InfoLabel>
              <InfoValue>{logData.temperature ?? 'N/A'}</InfoValue>
            </InfoRow>
            {logData.tokens_used !== null && logData.tokens_used !== undefined && (
              <InfoRow>
                <InfoLabel>Tokens Used:</InfoLabel>
                <InfoValue>{logData.tokens_used.toLocaleString()}</InfoValue>
              </InfoRow>
            )}
            {logData.input_tokens !== null && logData.input_tokens !== undefined && (
              <InfoRow>
                <InfoLabel>Input Tokens:</InfoLabel>
                <InfoValue>{logData.input_tokens.toLocaleString()}</InfoValue>
              </InfoRow>
            )}
            {logData.output_tokens !== null && logData.output_tokens !== undefined && (
              <InfoRow>
                <InfoLabel>Output Tokens:</InfoLabel>
                <InfoValue>{logData.output_tokens.toLocaleString()}</InfoValue>
              </InfoRow>
            )}
            {logData.price !== null && logData.price !== undefined && (
              <InfoRow>
                <InfoLabel>Cost:</InfoLabel>
                <InfoValue>${logData.price.toFixed(6)} USD</InfoValue>
              </InfoRow>
            )}
          </InfoGrid>
        </Section>

        <Section>
          <CollapsibleHeader onClick={() => toggleSection('prompt')}>
            <ChevronIcon expanded={expandedSections.prompt}>&#9654;</ChevronIcon>
            <SectionTitle>Prompt</SectionTitle>
          </CollapsibleHeader>
          {expandedSections.prompt && (
            <MarkdownContainer>
              <ReactMarkdown>{logData.prompt_text}</ReactMarkdown>
            </MarkdownContainer>
          )}
        </Section>

        {logData.response_text && (
          <Section>
            <CollapsibleHeader onClick={() => toggleSection('response')}>
              <ChevronIcon expanded={expandedSections.response}>&#9654;</ChevronIcon>
              <SectionTitle>Response</SectionTitle>
            </CollapsibleHeader>
            {expandedSections.response && <CodeBlock>{logData.response_text}</CodeBlock>}
          </Section>
        )}

        {logData.error_message && (
          <Section>
            <SectionTitle>Error</SectionTitle>
            <ErrorBox>{logData.error_message}</ErrorBox>
          </Section>
        )}

        {logData.context_data && (
          <Section>
            <CollapsibleHeader onClick={() => toggleSection('context')}>
              <ChevronIcon expanded={expandedSections.context}>&#9654;</ChevronIcon>
              <SectionTitle>Context Data</SectionTitle>
            </CollapsibleHeader>
            {expandedSections.context && (
              <CodeBlock>{JSON.stringify(logData.context_data, null, 2)}</CodeBlock>
            )}
          </Section>
        )}
      </ContentContainer>
    );
  };

  return (
    <ModalWrapper onClose={onClose} title="AI Prompt & Response Log" width={900} height={700}>
      {renderContent()}
    </ModalWrapper>
  );
};

export default AgentPromptLogModal;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  max-height: 620px;
  padding: 4px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const ChevronIcon = styled.span<{ expanded: boolean }>`
  font-size: 12px;
  color: #666;
  transition: transform 0.2s ease;
  transform: rotate(${(props) => (props.expanded ? '90deg' : '0deg')});
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(props) => {
    switch (props.status) {
      case 'success':
        return '#e8f5e9';
      case 'error':
        return '#ffebee';
      case 'timeout':
        return '#fff3e0';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'success':
        return '#2e7d32';
      case 'error':
        return '#c62828';
      case 'timeout':
        return '#e65100';
      default:
        return '#666';
    }
  }};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 16px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 6px;
`;

const InfoRow = styled.div`
  display: contents;
`;

const InfoLabel = styled.div`
  font-weight: 600;
  color: #666;
  font-size: 13px;
`;

const InfoValue = styled.div`
  color: #333;
  font-size: 13px;
  user-select: text;
`;

const MarkdownContainer = styled.div`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
  font-size: 13px;
  line-height: 1.5;
  overflow-x: auto;
  color: #333;
  user-select: text;

  /* Markdown element styling */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
  }

  h1 {
    font-size: 20px;
  }
  h2 {
    font-size: 18px;
  }
  h3 {
    font-size: 16px;
  }
  h4,
  h5,
  h6 {
    font-size: 14px;
  }

  p {
    margin: 8px 0;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  li {
    margin: 4px 0;
  }

  code {
    background-color: #e8e8e8;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
  }

  pre {
    background-color: #2d2d2d;
    color: #f8f8f2;
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  pre code {
    background-color: transparent;
    padding: 0;
    color: inherit;
    font-size: 12px;
  }

  blockquote {
    border-left: 4px solid #1976d2;
    padding-left: 12px;
    margin: 8px 0;
    color: #666;
    font-style: italic;
  }

  a {
    color: #1976d2;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  hr {
    border: none;
    border-top: 1px solid #ddd;
    margin: 16px 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }

  th {
    background-color: #e8e8e8;
    font-weight: 600;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }
`;

const CodeBlock = styled.pre`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  color: #333;
  user-select: text;
`;

const ErrorBox = styled.div`
  background-color: #ffebee;
  border: 1px solid #ef9a9a;
  border-radius: 6px;
  padding: 16px;
  color: #c62828;
  font-size: 13px;
  line-height: 1.5;
  user-select: text;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
`;

const ErrorText = styled.div`
  font-size: 16px;
  color: #f44336;
`;

const Timestamp = styled.div`
  font-size: 12px;
  color: #999;
  font-weight: 400;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RunTabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #1976d2;

  .MuiTabs-root {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const RunTabsLabel = styled.span`
  font-weight: 600;
  color: #1976d2;
  font-size: 14px;
  white-space: nowrap;
`;

const StyledTabs = styled(Tabs)`
  margin-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const StyledTab = styled(Tab)`
  min-width: 120px;
  text-transform: none;
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabStatus = styled.span<{ status: string }>`
  font-size: 16px;
  color: ${(props) => {
    switch (props.status) {
      case 'success':
        return '#2e7d32';
      case 'error':
        return '#c62828';
      default:
        return '#999';
    }
  }};
`;
